import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    AddingCard,
    CallToActionNullState,
    Dialog,
    DialogContent,
    Loading,
} from '../../../compositions';
import { closeDialog, openDialog } from '../../../helpers/dialog';
import { userHasRole } from '../../../helpers/role';
import trans from '../../../helpers/trans';
import { Skill, SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { SchoolExperience, SchoolExperienceFormData } from '../../../models/SchoolExperience/SchoolExperience';
import { SearchableOption, UserRole } from '../../../types';
import { SchoolExperienceForm, SkillsForm } from '../..';
import { SchoolExperienceItem } from './subcomponents';

import './SchoolExperiencesCard.scss';

interface SchoolExperiencesCardProps {
    isLoading: boolean;
    schoolExperienceIsLoading: boolean;
    schoolExperienceCreateIsSuccessful: boolean;
    schoolExperienceUpdateIsSuccessful: boolean;
    skillsIsLoading: boolean;
    skillsStepIsSuccessful: boolean;
    hasError?: boolean;
    schoolExperienceError: string;
    skillOptionsError: string;
    userRoles?: UserRole[];
    schoolExperiences: SchoolExperience[];
    skills: Skill[];
    fieldOfStudyOptions: SearchableOption[];
    skillOptions: SkillBase[];
    searchedSkillOptions: SkillBase[];
    onFieldOfStudyInput: (value: string) => void;
    onSelectFieldOfStudy: (value: string) => void;
    onAddSchoolExperience: (schoolExperienceFormData: SchoolExperienceFormData) => void;
    onEditSchoolExperience: (schoolExperienceFormData: SchoolExperienceFormData) => void;
    onDeleteSchoolExperience: (schoolExperience: SchoolExperience) => void;
    onSearchSkills: (query: string) => void;
    onAddSkills: (skillsFormData: SkillsFormData) => void;
    onIsSuccessfulReset: () => void;
    className?: string;
}

const SchoolExperiencesCard: FC<SchoolExperiencesCardProps> = ({
    isLoading,
    schoolExperienceIsLoading,
    schoolExperienceCreateIsSuccessful,
    schoolExperienceUpdateIsSuccessful,
    skillsIsLoading,
    skillsStepIsSuccessful,
    hasError,
    schoolExperienceError,
    skillOptionsError,
    userRoles = [],
    schoolExperiences,
    skills,
    fieldOfStudyOptions,
    skillOptions,
    searchedSkillOptions,
    onFieldOfStudyInput,
    onSelectFieldOfStudy,
    onAddSchoolExperience,
    onEditSchoolExperience,
    onDeleteSchoolExperience,
    onSearchSkills,
    onAddSkills,
    onIsSuccessfulReset,
    className = '',
}): ReactElement => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const numberOfFormSteps = 2;

    const [formStep, setFormStep] = useState<number>(1);
    const [schoolExperienceFormKey, setSchoolExperienceFormKey] = useState<number>(0);
    const [education, setEducation] = useState<string>('');

    const dialogStep = useMemo((): string => (
        formStep === 2 ? 'skills' : 'schoolExperience'
    ), [formStep]);

    const handleOpenDialog = (): void => openDialog(dialogRef);
    const handleCloseDialog = (): void => {
        closeDialog(dialogRef);

        setFormStep(1);
        setSchoolExperienceFormKey(schoolExperienceFormKey + 1);
    };

    const handleAddSchoolExperience = (schoolExperienceFormData: SchoolExperienceFormData): void => {
        setEducation(schoolExperienceFormData.fieldOfStudy);

        onAddSchoolExperience(schoolExperienceFormData);
    };

    useEffect((): void => {
        if (schoolExperienceCreateIsSuccessful) {
            setFormStep(2);
        }
    }, [schoolExperienceCreateIsSuccessful]);

    useEffect((): void => {
        if (skillsStepIsSuccessful) {
            handleCloseDialog();
            onIsSuccessfulReset();
        }
    }, [skillsStepIsSuccessful]);

    return (
        <AddingCard
            isEditable={userHasRole(UserRole.candidate, userRoles) && schoolExperiences.length > 0}
            showFillLabel={schoolExperiences.length === 0 && !isLoading}
            title={trans('containers.schoolExperiencesCard.title')}
            item={trans('common.schoolExperience')}
            userRoles={userRoles}
            onAddClick={handleOpenDialog}
            className={`school-experiences-card ${className}`}
        >
            {isLoading && (
                <Loading className="school-experiences-card__loader" />
            )}

            {!isLoading && schoolExperiences.length === 0 && (
                <CallToActionNullState
                    isEditable={!hasError && userHasRole(UserRole.candidate, userRoles)}
                    icon="education"
                    paragraph={trans('containers.schoolExperiencesCard.nullState.paragraph')}
                    subParagraph={trans('containers.schoolExperiencesCard.nullState.subParagraph')}
                    buttonText={trans('containers.schoolExperiencesCard.nullState.callToAction')}
                    onButtonClick={handleOpenDialog}
                    className="school-experiences-card__null-state"
                />
            )}

            {!isLoading && schoolExperiences.length > 0 && (
                <ul className="school-experiences-card__school-experience-wrapper">
                    {schoolExperiences.map(schoolExperience => (
                        <SchoolExperienceItem
                            key={schoolExperience.id}
                            isLoading={schoolExperienceIsLoading}
                            isSuccessful={schoolExperienceCreateIsSuccessful || schoolExperienceUpdateIsSuccessful}
                            isEditable={userHasRole(UserRole.candidate, userRoles)}
                            schoolExperience={schoolExperience}
                            fieldOfStudyOptions={fieldOfStudyOptions}
                            error={schoolExperienceError}
                            onFieldOfStudyInput={onFieldOfStudyInput}
                            onSelectFieldOfStudy={onSelectFieldOfStudy}
                            onEdit={onEditSchoolExperience}
                            onDelete={onDeleteSchoolExperience}
                            onIsSuccessfulReset={onIsSuccessfulReset}
                            className="school-experiences-card__school-experience"
                        />
                    ))}
                </ul>
            )}

            <Dialog
                ref={dialogRef}
                onClose={handleCloseDialog}
                className="school-experiences-card__dialog"
            >
                <DialogContent
                    step={formStep}
                    totalSteps={numberOfFormSteps}
                    title={trans(`containers.schoolExperiencesCard.dialog.${dialogStep}.title`)}
                    description={trans(`containers.schoolExperiencesCard.dialog.${dialogStep}.description`)}
                >
                    {formStep === 1 && (
                        <SchoolExperienceForm
                            key={schoolExperienceFormKey}
                            isLoading={schoolExperienceIsLoading}
                            isSuccessful={schoolExperienceCreateIsSuccessful || schoolExperienceUpdateIsSuccessful}
                            fieldOfStudyOptions={fieldOfStudyOptions}
                            error={schoolExperienceError}
                            onFieldOfStudyInput={onFieldOfStudyInput}
                            onSelectFieldOfStudy={onSelectFieldOfStudy}
                            onSubmit={handleAddSchoolExperience}
                            onCancel={handleCloseDialog}
                        />
                    )}

                    {formStep === 2 && (
                        <SkillsForm
                            isLoading={skillsIsLoading}
                            showSearch={skillOptions.length === 0}
                            education={education}
                            activeSkills={skills}
                            skillOptions={skillOptions}
                            searchedSkillOptions={searchedSkillOptions}
                            error={skillOptionsError}
                            onSkillSearchChange={onSearchSkills}
                            onSubmit={onAddSkills}
                            onCancel={handleCloseDialog}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </AddingCard>
    );
};

export default SchoolExperiencesCard;
